import { useMemo, useState } from 'react';
import ReactEcharts from 'echarts-for-react';
import {
    useGetMostCallsQuery,
    useGetClaireBookingFailureReasonsQuery,
    useGetTransferredReasonsQuery,
} from '../../../../../store/excecutive/executiveApi';
import { useTheme } from '../../../../../theme-context';

const HOTEL_SUB = '921544e4-20c1-703b-c1d4-62f60c91cf49';
const STACK_SERIES_NAMES = [
    'Abandoned',
    'Transferred',
    'Booked',
    'Booking Failure',
    'Other Successful',
] as const;

const EXPERIMENT_PALETTE = [
    '#22C55E',
    '#F59E0B',
    '#06B6D4',
    '#8B5CF6',
    '#EC4899',
    '#0EA5E9',
    '#14B8A6',
    '#F97316',
    '#6366F1',
    '#9CA3AF',
] as const;

const STACK_SERIES_COLORS: Record<(typeof STACK_SERIES_NAMES)[number], string> = {
    Abandoned: EXPERIMENT_PALETTE[9],
    Transferred: EXPERIMENT_PALETTE[1],
    Booked: EXPERIMENT_PALETTE[0],
    'Booking Failure': EXPERIMENT_PALETTE[4],
    'Other Successful': EXPERIMENT_PALETTE[5],
};

const formatReasonLabel = (value?: string) => {
    if (!value) return 'Unknown';
    return value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const ClaireMostCallsGraph = () => {
    const { theme } = useTheme();
    const [view, setView] = useState<'7day' | '30day' | 'today'>('7day');
    const [legendSelected, setLegendSelected] = useState<
        Record<(typeof STACK_SERIES_NAMES)[number], boolean>
    >({
        Abandoned: true,
        Transferred: true,
        Booked: true,
        'Booking Failure': true,
        'Other Successful': true,
    });

    const pollMs = 7000;

    const { data, isLoading, isError } = useGetMostCallsQuery(
        {
            hotelSub: HOTEL_SUB,
            view,
        },
        { pollingInterval: pollMs },
    );
    const {
        data: bookingFailureData,
        isLoading: bookingFailureLoading,
        isError: bookingFailureError,
    } = useGetClaireBookingFailureReasonsQuery(
        {
            hotelSub: HOTEL_SUB,
            view,
        },
        { pollingInterval: pollMs },
    );
    const {
        data: transferredReasonsData,
        isLoading: transferredReasonsLoading,
        isError: transferredReasonsError,
    } = useGetTransferredReasonsQuery(
        {
            hotelSub: HOTEL_SUB,
            view,
        },
        { pollingInterval: pollMs },
    );

    const option = useMemo(() => {
        if (!data?.data?.length) return null;

        const labels = data.data.map((item) => {
            if (!item.date) return '';
            const parsed = new Date(item.date);
            if (Number.isNaN(parsed.getTime())) return item.date;
            return parsed.toLocaleDateString('en-US', { weekday: 'short' });
        });

        const abandoned = data.data.map((item) => item.abandoned || 0);
        const transferred = data.data.map((item) => item.transferred || 0);
        const booked = data.data.map((item) => item.endingWithBooking || 0);
        const bookingFailure = data.data.map((item) => item.bookingFailure || 0);
        const otherSuccessful = data.data.map((item) => item.otherSuccessful || 0);

        const seriesData = [
            abandoned,
            transferred,
            booked,
            bookingFailure,
            otherSuccessful,
        ];
        const totals = data.data.map(
            (_, idx) =>
                (legendSelected.Abandoned ? abandoned[idx] : 0) +
                (legendSelected.Transferred ? transferred[idx] : 0) +
                (legendSelected.Booked ? booked[idx] : 0) +
                (legendSelected['Booking Failure'] ? bookingFailure[idx] : 0) +
                (legendSelected['Other Successful'] ? otherSuccessful[idx] : 0),
        );
        const topSeriesIndex = totals.map((total, idx) => {
            if (total === 0) return -1;
            for (let i = seriesData.length - 1; i >= 0; i -= 1) {
                const seriesName = STACK_SERIES_NAMES[i];
                if (legendSelected[seriesName] && seriesData[i][idx] > 0) return i;
            }
            return -1;
        });

        const getTotalLabel = (seriesIdx: number) => (params: any) => {
            const dataIdx = params.dataIndex;
            if (topSeriesIndex[dataIdx] !== seriesIdx) return '';
            return `${totals[dataIdx]}`;
        };

        const totalLabelStyle = {
            show: true,
            position: 'top' as const,
            color: theme === 'dark' ? '#e5e7eb' : '#111827',
            fontSize: 12,
            fontWeight: 600,
            formatter: (_params: any) => '',
        };

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
            },
            legend: {
                bottom: 0,
                textStyle: {
                    color: theme === 'dark' ? '#d1d5db' : '#374151',
                },
                selected: legendSelected,
                data: [...STACK_SERIES_NAMES],
            },
            grid: {
                left: '4%',
                right: '3%',
                top: '8%',
                bottom: '18%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: {
                    color: theme === 'dark' ? '#d1d5db' : '#374151',
                },
                axisLine: {
                    lineStyle: {
                        color: theme === 'dark' ? '#4b5563' : '#d1d5db',
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: 'dashed',
                        color: theme === 'dark' ? '#374151' : '#e5e7eb',
                    },
                },
            },
            yAxis: {
                type: 'value',
                name: '',
                nameTextStyle: {
                    color: theme === 'dark' ? '#d1d5db' : '#374151',
                },
                axisLabel: {
                    color: theme === 'dark' ? '#d1d5db' : '#374151',
                },
                splitLine: {
                    lineStyle: {
                        type: 'dashed',
                        color: theme === 'dark' ? '#374151' : '#e5e7eb',
                    },
                },
            },
            series: [
                {
                    name: 'Abandoned',
                    type: 'bar',
                    stack: 'total',
                    data: abandoned,
                    itemStyle: { color: STACK_SERIES_COLORS.Abandoned },
                    label: {
                        ...totalLabelStyle,
                        formatter: getTotalLabel(0),
                    },
                },
                {
                    name: 'Transferred',
                    type: 'bar',
                    stack: 'total',
                    data: transferred,
                    itemStyle: { color: STACK_SERIES_COLORS.Transferred },
                    label: {
                        ...totalLabelStyle,
                        formatter: getTotalLabel(1),
                    },
                },
                {
                    name: 'Booked',
                    type: 'bar',
                    stack: 'total',
                    data: booked,
                    itemStyle: { color: STACK_SERIES_COLORS.Booked },
                    label: {
                        ...totalLabelStyle,
                        formatter: getTotalLabel(2),
                    },
                },
                {
                    name: 'Booking Failure',
                    type: 'bar',
                    stack: 'total',
                    data: bookingFailure,
                    itemStyle: { color: STACK_SERIES_COLORS['Booking Failure'] },
                    label: {
                        ...totalLabelStyle,
                        formatter: getTotalLabel(3),
                    },
                },
                {
                    name: 'Other Successful',
                    type: 'bar',
                    stack: 'total',
                    data: otherSuccessful,
                    itemStyle: { color: STACK_SERIES_COLORS['Other Successful'] },
                    label: {
                        ...totalLabelStyle,
                        formatter: getTotalLabel(4),
                    },
                },
            ],
        };
    }, [data, theme, legendSelected]);

    const chartEvents = useMemo(
        () => ({
            legendselectchanged: (params: any) => {
                setLegendSelected(params.selected);
            },
        }),
        [],
    );

    const donutOption = useMemo(() => {
        return {
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => {
                    const percentage =
                        params?.data?.percentage !== undefined
                            ? Number(params.data.percentage).toFixed(2)
                            : params?.percent?.toFixed?.(2) || '0.00';
                    return `${params.name}: ${params.value} (${percentage}%)`;
                },
            },
            color: [...EXPERIMENT_PALETTE],
            graphic: [
                {
                    type: 'text',
                left: '50%',
                    top: '46%',
                    style: {
                        text: `${bookingFailureData?.totalFailures || 0}`,
                        fill: theme === 'dark' ? '#ffffff' : '#111827',
                        fontSize: 26,
                        fontWeight: 700,
                        textAlign: 'center',
                    },
                },
                {
                    type: 'text',
                left: '50%',
                    top: '57%',
                    style: {
                        text: 'TOPICS',
                        fill: '#6B7280',
                        fontSize: 12,
                        fontWeight: 600,
                        textAlign: 'center',
                    },
                },
            ],
            series: [
                {
                    type: 'pie',
                    radius: ['50%', '72%'],
                center: ['50%', '50%'],
                    avoidLabelOverlap: true,
                    label: { show: false },
                    labelLine: { show: false },
                    data:
                        bookingFailureData?.reasons?.map((item) => ({
                            name: formatReasonLabel(item.reason),
                            value: item.count,
                            percentage: item.percentage,
                        })) || [],
                },
            ],
        };
    }, [bookingFailureData, theme]);

    const transferDonutOption = useMemo(() => {
        return {
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => {
                    const percentage =
                        params?.data?.percentage !== undefined
                            ? Number(params.data.percentage).toFixed(2)
                            : params?.percent?.toFixed?.(2) || '0.00';
                    return `${params.name}: ${params.value} (${percentage}%)`;
                },
            },
            color: [...EXPERIMENT_PALETTE],
            graphic: [
                {
                    type: 'text',
                left: '50%',
                    top: '46%',
                    style: {
                        text: `${transferredReasonsData?.totalTransferred || 0}`,
                        fill: theme === 'dark' ? '#ffffff' : '#111827',
                        fontSize: 26,
                        fontWeight: 700,
                        textAlign: 'center',
                    },
                },
                {
                    type: 'text',
                left: '50%',
                    top: '57%',
                    style: {
                        text: 'TOPICS',
                        fill: '#6B7280',
                        fontSize: 12,
                        fontWeight: 600,
                        textAlign: 'center',
                    },
                },
            ],
            series: [
                {
                    type: 'pie',
                    radius: ['50%', '72%'],
                center: ['50%', '50%'],
                    avoidLabelOverlap: true,
                    label: { show: false },
                    labelLine: { show: false },
                    data:
                        transferredReasonsData?.reasons?.map((item) => ({
                            name: formatReasonLabel(item.reason),
                            value: item.count,
                            percentage: item.percentage,
                        })) || [],
                },
            ],
        };
    }, [transferredReasonsData, theme]);

    return (
        <div className="w-full mt-12 mb-4">
            <div className="flex flex-col md:flex-row gap-3 md:items-start">
                <div>
                    <h4
                        className="mb-1 text-[20px] leading-[20px] font-semibold tracking-[0px] text-[#101010] dark:text-white"
                        style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                        Call Analysis
                    </h4>
                </div>
                <div className="md:ml-auto relative w-full max-w-[300px] md:w-[300px] h-[40px] rounded-full bg-[#00330F] p-[3px] grid grid-cols-3 overflow-hidden">
                    <span
                        className="absolute top-[3px] bottom-[3px] left-[3px] rounded-full bg-white transition-transform duration-300 ease-out"
                        style={{
                            width: 'calc((100% - 6px) / 3)',
                            transform: `translateX(${
                                view === 'today'
                                    ? '0%'
                                    : view === '7day'
                                    ? '100%'
                                    : '200%'
                            })`,
                        }}
                    />
                    {[
                        { label: 'Today', value: 'today' as const },
                        { label: '7 Day', value: '7day' as const },
                        { label: '30 Day', value: '30day' as const },
                    ].map((item) => (
                        <button
                            key={item.value}
                            onClick={() => setView(item.value)}
                            className={`relative z-10 h-full px-1 text-[14px] leading-[20px] font-medium text-center transition-colors ${
                                view === item.value
                                    ? 'text-[#0A0A0A]'
                                    : 'text-white'
                            }`}
                            style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-4 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
                <h5 className="text-[#1C1C1C] dark:text-primary-dark font-semibold text-sm mb-3">
                    Most Calls Graph
                </h5>
                {isLoading ? (
                    <div className="h-72 flex items-center justify-center text-sm text-gray-500">
                        Loading chart...
                    </div>
                ) : isError ? (
                    <div className="h-72 flex items-center justify-center text-sm text-red-500">
                        Failed to load chart data
                    </div>
                ) : !option ? (
                    <div className="h-72 flex items-center justify-center text-sm text-gray-500">
                        No data available
                    </div>
                ) : (
                    <ReactEcharts
                        option={option}
                        onEvents={chartEvents}
                        style={{ height: 420, width: '100%' }}
                    />
                )}
            </div>

            <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
                    <h5 className="text-[#1C1C1C] dark:text-primary-dark font-semibold text-sm mb-3">
                        Transferred Calls Reasons
                    </h5>
                    {transferredReasonsLoading ? (
                        <div className="h-60 flex items-center justify-center text-sm text-gray-500">
                            Loading transferred reasons chart...
                        </div>
                    ) : transferredReasonsError ? (
                        <div className="h-60 flex items-center justify-center text-sm text-red-500">
                            Failed to load transferred reasons chart
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
                            <div className="min-w-0 rounded-lg border border-gray-100 dark:border-gray-800 p-4">
                                <p className="text-base font-semibold text-gray-800 dark:text-gray-100">
                                    Categories
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    Total topics: {transferredReasonsData?.totalTransferred || 0}
                                </p>
                                <div className="space-y-4">
                                    {transferredReasonsData?.reasons?.map((item, index) => (
                                        <div key={`${item.reason}-${index}`}>
                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                {formatReasonLabel(item.reason)}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {item.count} ({Number(item.percentage || 0).toFixed(1)}%)
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="min-w-0 w-full overflow-hidden">
                                <p className="text-center text-base font-medium text-[#5B9BD5] dark:text-[#93C5FD] mb-1">
                                    All Reasons
                                </p>
                                <ReactEcharts
                                    option={transferDonutOption}
                                    style={{ height: 280, width: '100%' }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
                    <h5 className="text-[#1C1C1C] dark:text-primary-dark font-semibold text-sm mb-3">
                        Booking Failure Reasons
                    </h5>
                    {bookingFailureLoading ? (
                        <div className="h-60 flex items-center justify-center text-sm text-gray-500">
                            Loading booking failure chart...
                        </div>
                    ) : bookingFailureError ? (
                        <div className="h-60 flex items-center justify-center text-sm text-red-500">
                            Failed to load booking failure chart
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
                            <div className="min-w-0 rounded-lg border border-gray-100 dark:border-gray-800 p-4">
                                <p className="text-base font-semibold text-gray-800 dark:text-gray-100">
                                    Booking Failure Reasons
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    Total topics: {bookingFailureData?.totalFailures || 0}
                                </p>
                                <div className="space-y-4">
                                    {bookingFailureData?.reasons?.map((item, index) => (
                                        <div key={`${item.reason}-${index}`}>
                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                {formatReasonLabel(item.reason)}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {item.count} ({Number(item.percentage || 0).toFixed(1)}%)
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="min-w-0 w-full overflow-hidden">
                                <p className="text-center text-base font-medium text-[#5B9BD5] dark:text-[#93C5FD] mb-1">
                                    All Reasons
                                </p>
                                <ReactEcharts
                                    option={donutOption}
                                    style={{ height: 280, width: '100%' }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClaireMostCallsGraph;
